<!--
  Coloured note / alert block — the `sbi.ui.callout(body, opts?)` container.

  Renders the four `kind` variants with distinct leading icons (info `i`,
  success check, warn triangle, danger octagon). Icon is the primary
  signal — colour is reinforcement — so the visual stays legible for
  viewers with red-green colour blindness. A visually-hidden SR prefix
  ("Note: " / "Success: " / "Warning: " / "Error: ") carries the kind
  semantically.

  All kinds render with `role="note"` (Q11 decision). State-driven
  appearance of a `kind="danger"` callout will NOT auto-announce —
  documented accessibility trade-off; revisit if real viewer feedback
  surfaces.

  Embedder theming:
    - data-part="callout" / data-kind="info|success|warn|danger" — shadow-DOM-safe targeting hooks.
    - --lucent-ui-callout-{info,success,warn,danger}-bg     — background
    - --lucent-ui-callout-{info,success,warn,danger}-border — border colour
    - --lucent-ui-callout-{info,success,warn,danger}-fg     — text colour
    - --lucent-ui-callout-{info,success,warn,danger}-icon   — icon fill

  Defaults derive from PALETTE / COLORS in theme.ts: `success` → `--pos`,
  `danger` → `--neg`, `info` → `--lucent-status-info`, `warn` →
  `--lucent-status-warn`.
-->

<script setup lang="ts">
import { onMounted } from "vue";

import type { CalloutKind, ChangeValue } from "../sbi/protocol";
import type { TreeNode } from "../sbi/validate-render-op";
import InsightNode from "./InsightNode.vue";
import LucentIcon from "./LucentIcon.vue";

const props = defineProps<{
  calloutKind: CalloutKind;
  title?: string;
  /**
   * Optional override of the kind-default leading icon. When set, the
   * `LucentIcon` of this name is rendered in place of the inline SVG
   * the kind would otherwise emit. The colour band still follows
   * `calloutKind`; only the glyph is swapped.
   */
  icon?: string;
  children: ReadonlyArray<TreeNode>;
  loading?: boolean;
  formats?: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /** Forwarded from a descendant modal drilldown's X / Esc / backdrop. */
  (e: "chrome-click-target", id: string): void;
  /** Forwarded from descendant accordions so EmbedHost can surface the DOM event. */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /**
   * Fires once per Vue mount of this callout. Forwarded up the tree
   * and emitted as a DOM `callout-rendered` CustomEvent so integrators
   * can hook telemetry (e.g. badge a sidebar with danger-callout counts).
   * Patches that update props without remounting don't fire again;
   * conditional re-mounts (e.g. coming back from a `sbi.branch.when`
   * false-arm) do.
   *
   * `kind` is typed as `string` (not `CalloutKind`) so this signature
   * matches every wrapping component in the forwarding chain — the
   * Ajv enum on the host-side render-op schema is the real guarantee.
   */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();

onMounted(() => {
  emit("callout-rendered", props.calloutKind, props.title);
});

// Visually-hidden SR prefix per kind (Q12). Carried as `aria-label` on
// the icon so the kind is announced before the title / body content.
const srPrefix: Record<CalloutKind, string> = {
  info: "Note:",
  success: "Success:",
  warn: "Warning:",
  danger: "Error:",
};
const ariaLabel = (): string => srPrefix[props.calloutKind];
</script>

<template>
  <aside
    class="callout"
    :class="`callout--${calloutKind}`"
    :data-part="'callout'"
    :data-kind="calloutKind"
    role="note"
  >
    <div class="callout-icon" :aria-label="ariaLabel()" role="img">
      <!-- Override slot: when the author passed `opts.icon`, render that
           glyph from the curated set instead of the kind-default SVG.
           Colour band still follows calloutKind via the `.callout-icon`
           parent class.
      -->
      <LucentIcon v-if="icon" :name="icon" :size="14" />
      <!-- Per-kind icon. Inline SVG so the fill colour can be tokenised
           independently of the body text colour. -->
      <svg
        v-else-if="calloutKind === 'info'"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="8" cy="8" r="7" />
        <rect x="7" y="7" width="2" height="5" rx="1" fill="currentColor" />
        <circle cx="8" cy="4.5" r="1.1" fill="currentColor" />
      </svg>
      <svg
        v-else-if="calloutKind === 'success'"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="8" cy="8" r="7" />
        <path
          d="M4.5 8.5l2.5 2.5 4.5-5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else-if="calloutKind === 'warn'"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M8 1.5 L15 14 H1 Z" />
        <rect x="7.2" y="6" width="1.6" height="4.5" rx="0.7" fill="currentColor" />
        <circle cx="8" cy="12" r="0.9" fill="currentColor" />
      </svg>
      <svg
        v-else
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <!-- Octagon for danger — distinct silhouette from the warn triangle. -->
        <polygon points="5,1.5 11,1.5 14.5,5 14.5,11 11,14.5 5,14.5 1.5,11 1.5,5" />
        <path
          d="M5.5 5.5 L10.5 10.5 M10.5 5.5 L5.5 10.5"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </div>
    <div class="callout-body">
      <div v-if="title" class="callout-title">{{ title }}</div>
      <div class="callout-children">
        <InsightNode
          v-for="(child, i) in children"
          :key="i"
          :node="child"
          :loading="loading"
          :formats="formats"
          @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
          @change-target="(id, value) => $emit('change-target', id, value)"
          @chrome-click-target="(id) => $emit('chrome-click-target', id)"
          @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
          @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
        />
      </div>
    </div>
  </aside>
</template>

<style scoped>
.callout {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid;
  border-radius: var(--radius);
  margin: var(--space-2) 0;
  min-width: 0;
}
.callout-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  margin-top: 1px;
}
.callout-icon svg {
  width: 100%;
  height: 100%;
}
.callout-body {
  min-width: 0;
}
.callout-title {
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: -0.01em;
  margin-bottom: 4px;
  line-height: 1.3;
}
.callout-children {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  line-height: 1.5;
}
/* Pull nested text-card border off so the callout's frame is the only
   one — otherwise "callout containing markdown" reads as nested boxes. */
.callout-children :deep(.text-card) {
  background: transparent;
  border: 0;
  padding: 0;
}

/* Per-kind colour tokens. Each variant fills bg / border / fg / icon
   from its own token family, with palette-aware fallbacks. */
.callout--info {
  background: var(--lucent-ui-callout-info-bg, rgba(91, 156, 242, 0.12));
  border-color: var(--lucent-ui-callout-info-border, rgba(91, 156, 242, 0.4));
  color: var(--lucent-ui-callout-info-fg, var(--card-ink));
}
.callout--info .callout-icon {
  color: var(--lucent-ui-callout-info-icon, #5b9cf2);
  fill: var(--lucent-ui-callout-info-icon, #5b9cf2);
}
.callout--success {
  background: var(--lucent-ui-callout-success-bg, rgba(58, 169, 136, 0.12));
  border-color: var(--lucent-ui-callout-success-border, rgba(58, 169, 136, 0.4));
  color: var(--lucent-ui-callout-success-fg, var(--card-ink));
}
.callout--success .callout-icon {
  color: var(--lucent-ui-callout-success-icon, #3aa988);
  fill: var(--lucent-ui-callout-success-icon, #3aa988);
}
.callout--warn {
  background: var(--lucent-ui-callout-warn-bg, rgba(243, 201, 74, 0.18));
  border-color: var(--lucent-ui-callout-warn-border, rgba(243, 201, 74, 0.55));
  color: var(--lucent-ui-callout-warn-fg, var(--card-ink));
}
.callout--warn .callout-icon {
  color: var(--lucent-ui-callout-warn-icon, #b8881a);
  fill: var(--lucent-ui-callout-warn-icon, #b8881a);
}
.callout--danger {
  background: var(--lucent-ui-callout-danger-bg, rgba(217, 99, 99, 0.12));
  border-color: var(--lucent-ui-callout-danger-border, rgba(217, 99, 99, 0.45));
  color: var(--lucent-ui-callout-danger-fg, var(--card-ink));
}
.callout--danger .callout-icon {
  color: var(--lucent-ui-callout-danger-icon, #d96363);
  fill: var(--lucent-ui-callout-danger-icon, #d96363);
}

/* Screen-reader-only utility for the `aria-label` on the icon div. */
:where([role="img"][aria-label]) {
  position: relative;
}
</style>
